import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  T_FetchCountDetails_Response_Object,
  T_LiBankFormData,
  T_LifeInsuranceSliceState,
  T_PolBankDetails,
} from "./LifeInsurance_Types";
import { T_BackgroundImage } from "../../globalTypes/GlobalTypes";

const initialState: T_LifeInsuranceSliceState = {
  dashboardDetails: [],
  dashboardDetailsLength: 0,
  secondaryDashboardDetails: [],
  dashboardFundDetails: [],
  policyAllDetails: [],
  serviceMenuList: [],
  policyDetails: [],
  fundDetails: {},
  pastPaymentDetails: {},
  polBankDetails: [],
  premiumReceiptDetails: [],
  annuityDetails: [],
  retryDashboardDetails: 0,
  retrySecondaryDashboardDetails: 0,
  npsZoneDetails: [],
  isPanAadharLinkingCaseCreateCalled: false,
  isReKycCaseCreateCalled: false,
  reKyc: { eligibility: false, mandatory: false },
  countDetails: [],
  liGetEmailClickCount: {} as Record<string, Record<string, number>>,
  liBankFormData: null,
  backgroundImages: [],
  shouldOpenMyServicesPopup: false,
};

export const LifeInsuranceSlice = createSlice({
  name: "LifeInsuranceSlice",
  initialState,
  reducers: {
    setLiDashboardDetails: (state, action) => {
      return {
        ...state,
        dashboardDetails: action.payload,
      };
    },
    setLiDashboardDetailsLength: (state, action) => {
      return {
        ...state,
        dashboardDetailsLength: action.payload,
      };
    },
    setSecondaryLiDashboardDetails: (state, action) => {
      return {
        ...state,
        secondaryDashboardDetails: action.payload,
      };
    },
    setLiDashboardFundDetails: (state, action) => {
      return {
        ...state,
        dashboardFundDetails: action.payload,
      };
    },
    setServiceMenuDetailsList: (state, action) => {
      return {
        ...state,
        serviceMenuList: action.payload,
      };
    },
    setLiSecondaryDashboardFundDetails: (state, action) => {
      return {
        ...state,
        dashboardFundDetails: [
          ...state.dashboardFundDetails,
          ...action.payload,
        ],
      };
    },
    setLiPolicyAllDetails: (state, action) => {
      return {
        ...state,
        policyAllDetails: action.payload,
      };
    },
    setLiPolicyDetails: (state, action) => {
      return {
        ...state,
        policyDetails: action.payload,
      };
    },
    setLiFundDetails: (state, action) => {
      return {
        ...state,
        fundDetails: action.payload,
      };
    },
    setLiPastPaymentDetails: (state, action) => {
      return {
        ...state,
        pastPaymentDetails: action.payload,
      };
    },
    setLiPolBankDetails: (state, action: PayloadAction<T_PolBankDetails[]>) => {
      return {
        ...state,
        polBankDetails: action.payload,
      };
    },
    setLiPremiumReceiptDetails: (state, action) => {
      return {
        ...state,
        premiumReceiptDetails: action.payload,
      };
    },
    setLiAnnuityDetails: (state, action) => {
      return {
        ...state,
        annuityDetails: action.payload,
      };
    },
    retryDashboardDetails: (state) => {
      const incrementCount =
        (state?.retryDashboardDetails ? state?.retryDashboardDetails : 0) + 1;
      return {
        ...state,
        retryDashboardDetails: incrementCount,
      };
    },
    retrySecondaryDashboardDetails: (state) => {
      const incrementCount =
        (state?.retrySecondaryDashboardDetails
          ? state?.retrySecondaryDashboardDetails
          : 0) + 1;
      return {
        ...state,
        retrySecondaryDashboardDetails: incrementCount,
      };
    },
    setNpsZoneDetails: (state, action) => {
      return {
        ...state,
        npsZoneDetails: action.payload,
      };
    },
    setIsPanAadharLinkingCaseCreateCalled: (state, action) => {
      return {
        ...state,
        isPanAadharLinkingCaseCreateCalled: action.payload,
      };
    },
    setIsReKycCaseCreateCalled: (state, action) => {
      return {
        ...state,
        isReKycCaseCreateCalled: action.payload,
      };
    },
    updateReKycFlags: (state, action) => {
      return {
        ...state,
        reKyc: { ...state.reKyc, ...action.payload },
      };
    },
    updateCountDetails: (
      state,
      action: PayloadAction<T_FetchCountDetails_Response_Object[]>,
    ) => {
      return {
        ...state,
        countDetails: action.payload,
      };
    },
    setLiGetEmailCount: (
      state,
      action: PayloadAction<{
        policyNumber: string;
        featureName: string;
        count: number;
      }>,
    ) => {
      const { policyNumber, featureName, count } = action.payload;
      if (!state.liGetEmailClickCount[policyNumber]) {
        state.liGetEmailClickCount[policyNumber] = {};
      }
      state.liGetEmailClickCount[policyNumber][featureName] = count;
    },

    updateLiGetEmailCount: (
      state,
      action: PayloadAction<{ policyNumber: string; featureName: string }>,
    ) => {
      const { policyNumber, featureName } = action.payload;
      if (!state.liGetEmailClickCount[policyNumber]) {
        state.liGetEmailClickCount[policyNumber] = {};
      }
      const current =
        state.liGetEmailClickCount[policyNumber][featureName] ??
        state.countDetails
          ?.find((item) => item.policyNumber === policyNumber)
          ?.countDetails?.find((c) => c.functionalityCode === featureName)
          ?.entryCount ??
        0;

      state.liGetEmailClickCount[policyNumber][featureName] = current + 1;
    },
    setLiBankFormData: (state, action: PayloadAction<T_LiBankFormData>) => {
      state.liBankFormData = action.payload;
    },

    clearLiBankFormData: (state) => {
      state.liBankFormData = null;
    },
    setLiBackgroundImage: (state, action: PayloadAction<T_BackgroundImage>) => {
      const existingBackgroundImages = Array.isArray(state.backgroundImages)
        ? state.backgroundImages
        : [];

      return {
        ...state,
        backgroundImages: [...existingBackgroundImages, action.payload],
      };
    },
    setShouldOpenLiMyServicesPopup: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        shouldOpenMyServicesPopup: action.payload,
      };
    },
  },
});

export const LifeInsuranceReducer = LifeInsuranceSlice.reducer;
export const LifeInsuranceSliceKey = LifeInsuranceSlice.reducerPath;
export const {
  setLiDashboardDetails,
  setLiDashboardDetailsLength,
  setLiDashboardFundDetails,
  setLiPolicyAllDetails,
  setLiPolicyDetails,
  setLiFundDetails,
  setLiPolBankDetails,
  setLiPastPaymentDetails,
  setLiPremiumReceiptDetails,
  setServiceMenuDetailsList,
  setLiAnnuityDetails,
  setSecondaryLiDashboardDetails,
  retryDashboardDetails,
  retrySecondaryDashboardDetails,
  setLiSecondaryDashboardFundDetails,
  setNpsZoneDetails,
  setIsPanAadharLinkingCaseCreateCalled,
  setIsReKycCaseCreateCalled,
  updateReKycFlags,
  updateCountDetails,
  setLiGetEmailCount,
  updateLiGetEmailCount,
  setLiBankFormData,
  clearLiBankFormData,
  setLiBackgroundImage,
  setShouldOpenLiMyServicesPopup,
} = LifeInsuranceSlice.actions;
